import { FormikProvider, useFormik } from 'formik'

import {
  HourlyRateIncome,
  IncomeType,
  PeriodicIncome,
  PeriodicTimePeriod,
} from '@/features/goal-setting/IncomeCalculator'
import { Button, Dialog, Form, Text } from '@/ui'
import { getFieldName, makeReqStringSchema } from '@/base'
import { TypographyStyles } from '@/styles'

export const IncomeCalculatorDialog = ({
  onCancel,
  onSubmit,
  incomeType,
}: {
  onCancel: () => void
  onSubmit: (income: HourlyRateIncome | PeriodicIncome) => void
  incomeType: IncomeType
}) => {
  const formik = useFormik({
    initialValues: {
      incomeTypeName: '',
      period: 'perMonth' as PeriodicTimePeriod,
    },
    onSubmit: ({ incomeTypeName, period }) => {
      if (incomeType === IncomeType.hourlyRate) {
        onSubmit({
          incomeType: IncomeType.hourlyRate,
          name: incomeTypeName,
          rate: 0,
          amountPerPeriod: 0,
          period,
        })
      } else {
        onSubmit({
          incomeType: IncomeType.periodic,
          name: incomeTypeName,
          rate: 0,
          amountPerPeriod: null,
          period,
        })
      }
    },
  })

  const { submitForm } = formik

  return (
    <Dialog open onClose={onCancel}>
      <Dialog.Content>
        <FormikProvider value={formik}>
          <Dialog.Title>
            Add{' '}
            {incomeType === IncomeType.hourlyRate
              ? 'hourly income'
              : 'periodic income'}
          </Dialog.Title>

          {incomeType === IncomeType.hourlyRate ? (
            <Text variant={TypographyStyles.BODY_SM} className="mt-6">
              Adding hourly income enables you to track income that you get paid
              at an hourly rate. You can track hours worked per week, per month,
              or per year.
            </Text>
          ) : (
            <Text variant={TypographyStyles.BODY_SM} className="mt-6">
              Adding periodic income enables you to track income that you get
              paid a set amount every period (e.g. per week, per month, or per
              year).
            </Text>
          )}

          <Form.Input
            name={getFieldName<typeof formik.values>('incomeTypeName')}
            label="Income Type Name"
            placeholder="Enter text"
            schema={makeReqStringSchema()}
            wrapperClassName="mt-4"
          />

          <Form.Select
            name={getFieldName<typeof formik.values>('period')}
            options={[
              { value: 'perWeek', text: 'Per week' },
              { value: 'perMonth', text: 'Per month' },
              { value: 'perYear', text: 'Per year' },
            ]}
            label="Period"
            schema={makeReqStringSchema()}
            wrapperClassName="mt-4"
          />
          <Dialog.Footer>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={submitForm}>Save</Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
